import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import AuthProvider from './contexts/AuthContext'
import { useAuth } from './contexts/AuthContext'
import { Toaster } from 'sonner'

// Pages
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import PatientDashboard from './pages/PatientDashboard'
import DoctorDashboard from './pages/DoctorDashboard'


// Components
import Loading from './components/Loading'

// Componente para rotas protegidas
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, userProfile, loading } = useAuth()

  console.log('🛡️ ProtectedRoute - loading:', loading, 'user:', !!user, 'profile:', userProfile?.role)

  if (loading) {
    return <Loading fullScreen text="Verificando autenticação..." />
  }

  if (!user || !userProfile) {
    console.log('🚫 ProtectedRoute - Redirecionando para login')
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && !allowedRoles.includes(userProfile.role)) {
    console.log('🚫 ProtectedRoute - Role não autorizada:', userProfile.role)
    return <Navigate to="/" replace />
  }

  console.log('✅ ProtectedRoute - Acesso autorizado')
  return <>{children}</>
}

// Componente para rotas públicas (apenas usuários não logados)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, userProfile, loading } = useAuth()

  console.log('🌐 PublicRoute - loading:', loading, 'user:', !!user, 'profile:', userProfile?.role)

  if (loading) {
    return <Loading fullScreen text="Verificando autenticação..." />
  }

  if (user && userProfile) {
    // Logs informativos conforme solicitado
    if (userProfile.role === 'patient') {
      console.log('✅ Usuário já logado (paciente), redirecionando para dashboard do paciente')
      return <Navigate to="/patient" replace />
    } else if (userProfile.role === 'doctor') {
      console.log('✅ Usuário já logado (médico), redirecionando para dashboard do médico')
      return <Navigate to="/doctor" replace />
    } else {
      console.log('✅ Usuário já logado (role desconhecida), redirecionando para home')
      return <Navigate to="/" replace />
    }
  }

  console.log('ℹ️ Usuário não logado, mantendo na tela de login')
  return <>{children}</>
}

// Componente para redirecionamento da raiz
function RootRedirect() {
  const { user, userProfile, loading } = useAuth()

  console.log('🏠 RootRedirect - loading:', loading, 'user:', !!user, 'profile:', userProfile?.role)

  // Aguardar carregamento completo
  if (loading) {
    console.log('⏳ RootRedirect - Aguardando carregamento...')
    return <Loading fullScreen text="Carregando..." />
  }

  // Se não há usuário ou perfil, redirecionar para home
  if (!user || !userProfile) {
    console.log('🔄 RootRedirect - Sem usuário/perfil, redirecionando para home')
    return <Navigate to="/home" replace />
  }

  // Redirecionar baseado no role do usuário
  console.log('👤 RootRedirect - Usuário autenticado com role:', userProfile.role)
  
  switch (userProfile.role) {
    case 'patient':
      console.log('🔄 RootRedirect - Redirecionando paciente para /patient')
      return <Navigate to="/patient" replace />
    case 'doctor':
      console.log('🔄 RootRedirect - Redirecionando médico para /doctor')
      return <Navigate to="/doctor" replace />
    default:
      console.log('⚠️ RootRedirect - Role desconhecida:', userProfile.role, '- redirecionando para home')
      return <Navigate to="/home" replace />
  }
}

// Componente principal de roteamento
function AppRoutes() {
  return (
    <Routes>
      {/* Rota raiz com redirecionamento inteligente */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Rota home para usuários não logados */}
      <Route path="/home" element={
        <PublicRoute>
          <Home />
        </PublicRoute>
      } />
      
      {/* Rotas públicas (apenas usuários não logados) */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Rotas protegidas */}
      <Route path="/patient" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <PatientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/doctor" element={
        <ProtectedRoute allowedRoles={['doctor']}>
          <DoctorDashboard />
        </ProtectedRoute>
      } />

      
      {/* Rota de redirecionamento para usuários logados */}
      <Route path="/dashboard" element={<RootRedirect />} />
      
      {/* Rota 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

// Componente principal da aplicação
function App() {
  console.log('🚀 App iniciando...')
  
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <AppRoutes />
        </div>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          duration={4000}
        />
      </Router>
    </AuthProvider>
  )
}

export default App